import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Check, X, Printer, RefreshCw, User, Calendar, ArrowLeft } from "lucide-react";
import { isValidQuotationCode } from "@/utils/quotation-utils";

// Define proper interface for quotation data
interface QuotationData {
  id: string;
  code: string;
  created_at: string;
  description: string;
  status?: string;
  total_amount: number;
  discounts?: number;
  taxes?: number;
  payment_method?: string;
  validity_date: string;
  execution_deadline?: string;
  materials?: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  viewed_at?: string | null;
  customer?: {
    id: string;
    name: string;
    phone?: string;
    whatsapp?: string;
    description?: string;
  } | null;
  creator_email?: string;
  created_by?: string;
}
export const QuotationView = () => {
  const {
    code
  } = useParams();
  const [quotation, setQuotation] = useState<QuotationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    toast
  } = useToast();
  const navigate = useNavigate();

  // Function to clear the quotations cache in service worker
  const clearQuotationsCache = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      console.log("Sending message to clear quotations cache");
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_QUOTATIONS_CACHE'
      });
    }
  };

  // Fetch quotation data via secure edge function
  const fetchQuotation = async (skipCache = false) => {
    try {
      setIsLoading(true);
      setError(null);
      if (!isValidQuotationCode(code)) {
        setError("Código do orçamento inválido ou não fornecido");
        setIsLoading(false);
        return;
      }
      if (skipCache) clearQuotationsCache();

      const { data, error: fnError } = await supabase.functions.invoke(
        "get-public-quotation",
        { body: { code } }
      );
      if (fnError) throw new Error("Erro ao carregar orçamento. Tente novamente mais tarde.");
      if (!data || data.error || !data.quotation) {
        throw new Error("Orçamento não encontrado. Verifique se o código está correto.");
      }
      setQuotation(data.quotation as QuotationData);
    } catch (error) {
      console.error("Error in fetchQuotation:", error);
      setError(error instanceof Error ? error.message : "Erro ao carregar orçamento. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    clearQuotationsCache(); // Clear cache first to ensure fresh data
    fetchQuotation();

    // Setup periodic refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      console.log("Running scheduled refresh for quotation");
      fetchQuotation(true);
    }, 30000);
    return () => clearInterval(refreshInterval);
  }, [code]);
  const handleRefresh = () => {
    setIsRefreshing(true);
    clearQuotationsCache();
    fetchQuotation(true); // Force refresh from server
  };
  const handleBack = () => {
    navigate(-1);
  };
  const handleAccept = async () => {
    try {
      if (!quotation) return;
      const { error } = await supabase.functions.invoke("get-public-quotation", {
        body: { code: quotation.code, action: "update_status", status: "accepted" },
      });
      if (error) throw error;
      clearQuotationsCache();
      fetchQuotation(true);
      toast({ title: "Sucesso", description: "Orçamento aceito com sucesso!" });
    } catch (error) {
      console.error("Error accepting quotation:", error);
      toast({ title: "Erro", description: "Erro ao aceitar orçamento", variant: "destructive" });
    }
  };
  const handleReject = async () => {
    try {
      if (!quotation) return;
      const { error } = await supabase.functions.invoke("get-public-quotation", {
        body: { code: quotation.code, action: "update_status", status: "rejected" },
      });
      if (error) throw error;
      clearQuotationsCache();
      fetchQuotation(true);
      toast({ title: "Aviso", description: "Orçamento recusado" });
    } catch (error) {
      console.error("Error rejecting quotation:", error);
      toast({ title: "Erro", description: "Erro ao recusar orçamento", variant: "destructive" });
    }
  };

  const handlePrint = () => {
    window.print();
  };
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0073B7] mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>;
  }
  if (error || !quotation) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Orçamento não encontrado</h2>
          <p className="text-gray-600 mb-6">{error || "Verifique se o código está correto"}</p>
          <img src="/lovable-uploads/5c280e34-5539-458e-bcfa-025fb865f39f.png" alt="Orçamento não encontrado" className="mx-auto mb-6 max-w-[200px]" />
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button onClick={handleBack} variant="outline" className="mb-2 sm:mb-0">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <Button onClick={handleRefresh} className="bg-[#0073B7] hover:bg-[#004080] text-white">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>;
  }
  const formattedDate = new Date(quotation.created_at).toLocaleDateString('pt-BR');
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6 print:hidden">
          <Button variant="outline" onClick={handleBack} className="flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          
          <div className="flex gap-2">
            <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline" className="flex items-center gap-1">
              {isRefreshing ? <div className="animate-spin h-4 w-4 border-2 border-t-transparent border-current rounded-full" /> : <RefreshCw className="h-4 w-4" />}
              <span>Atualizar</span>
            </Button>
            <Button onClick={handleAccept} className="bg-green-600 hover:bg-green-700">
              <Check className="h-4 w-4 mr-2" />
              Aceitar
            </Button>
            <Button onClick={handleReject} variant="destructive">
              <X className="h-4 w-4 mr-2" />
              Recusar
            </Button>
            <Button onClick={handlePrint} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <img src="/lovable-uploads/6a11bedc-e2ef-4189-a647-ecb2a90116bc.png" alt="Solutions Soldas" className="w-32 h-auto" />
            <div className="text-right text-sm text-gray-800 font-medium">
              <h3 className="font-bold text-[#004080] mb-1">Solucionar Soldas</h3>
              <p>Telefone: (34) 99250-5512 / (34) 99989483</p>
              <p>Email: contato@solucionarsoldas.com</p>
              <p>Rua Vicente Capoeira, 1153</p>
              <p>Presidente Olegário - MG</p>
            </div>
          </div>
          <div className="border-b-2 border-[#004080] my-4"></div>
        </div>

        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#004080]">Orçamento {quotation.code}</h1>
            <span className="text-sm text-gray-700">
              Data: {new Date(quotation.created_at).toLocaleDateString('pt-BR')}
            </span>
          </div>
          
          {/* Informações do criador */}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-[#004080] mb-2">Cliente</h3>
            {quotation.customer ? <p className="text-gray-800 font-medium text-lg">{quotation.customer.name}</p> : <p className="text-gray-600">Informações do cliente não disponíveis</p>}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-[#004080] mb-2">Descrição do Serviço</h3>
            <p className="text-gray-800">{quotation.description}</p>
          </div>

          {quotation.materials && quotation.materials.length > 0 && <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-[#004080] mb-4">Materiais</h3>
              <div className="space-y-3">
                {quotation.materials.map((material: any, index: number) => <div key={index} className="flex justify-between items-center text-gray-800 border-b border-gray-200 pb-3">
                    <div>
                      <p className="font-medium">{material.name}</p>
                      <p className="text-sm">Quantidade: {material.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">
                        Preço unitário: {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(material.price)}
                      </p>
                      <p className="font-medium">
                        Subtotal: {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(material.quantity * material.price)}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              {quotation.discounts > 0 && <div className="text-gray-800">
                  <p>Desconto: {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                }).format(quotation.discounts)}</p>
                </div>}
              {quotation.taxes > 0 && <div className="text-gray-800">
                  <p>Impostos: {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                }).format(quotation.taxes)}</p>
                </div>}
            </div>
            <div className="border-t border-gray-300 pt-4">
              <h3 className="font-semibold text-[#004080] mb-2">Valor Total</h3>
              <p className="text-3xl font-bold text-[#004080]">
                {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL"
              }).format(quotation.total_amount)}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-[#004080] mb-2">Forma de Pagamento</h3>
            <p className="text-gray-800">{quotation.payment_method}</p>
          </div>

          <div className="mt-8 pt-8 border-t text-sm">
            <p className="text-center text-gray-800">
              Este orçamento tem validade até {new Date(quotation.validity_date).toLocaleDateString('pt-BR')}
            </p>
            <p className="text-center mt-2 text-gray-800">
              Prazo de execução: {quotation.execution_deadline}
            </p>
            <div className="mt-8 text-center text-sm text-gray-800">
              <p className="font-medium mb-2">Para mais informações ou esclarecimentos, entre em contato:</p>
              <p>Telefone: (34) 99250-5512 / (34) 99989483</p>
              <p>Email: contato@solucionarsoldas.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default QuotationView;
